import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuthStore } from '@/stores/auth';

interface IdleTimeoutOptions {
  /** Idle time before logout in milliseconds (default: 30 minutes) */
  idleTimeout?: number;
  /** Time before logout to show warning in milliseconds (default: 2 minutes) */
  warningTime?: number;
  /** Callback when warning should be shown */
  onWarning?: (remainingSeconds: number) => void;
  /** Callback when user becomes idle (before logout) */
  onIdle?: () => void;
  /** Callback when user activity is detected after warning */
  onActive?: () => void;
  /** Whether the hook is enabled (default: true) */
  enabled?: boolean;
}

interface IdleTimeoutReturn {
  /** Whether the warning is currently showing */
  isWarning: boolean;
  /** Seconds remaining before logout (only during warning) */
  remainingSeconds: number;
  /** Manually reset the idle timer */
  resetTimer: () => void;
  /** Whether user is currently idle */
  isIdle: boolean;
}

const DEFAULT_IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const DEFAULT_WARNING_TIME = 2 * 60 * 1000;  // 2 minutes before logout

// Events that indicate user activity
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'wheel',
] as const;

export function useIdleTimeout(options: IdleTimeoutOptions = {}): IdleTimeoutReturn {
  const {
    idleTimeout = DEFAULT_IDLE_TIMEOUT,
    warningTime = DEFAULT_WARNING_TIME,
    onWarning,
    onIdle,
    onActive,
    enabled = true,
  } = options;

  const { logout, isAuthenticated } = useAuthStore();

  const [isWarning, setIsWarning] = useState(false);
  const [isIdle, setIsIdle] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // Handle logout
  const handleLogout = useCallback(() => {
    clearAllTimers();
    setIsWarning(false);
    setIsIdle(true);
    setRemainingSeconds(0);
    onIdle?.();
    logout();
  }, [clearAllTimers, logout, onIdle]);

  // Start warning countdown
  const startWarningCountdown = useCallback(() => {
    setIsWarning(true);
    const seconds = Math.floor(warningTime / 1000);
    setRemainingSeconds(seconds);
    onWarning?.(seconds);

    // Countdown interval
    let remaining = seconds;
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setRemainingSeconds(remaining);

      if (remaining <= 0) {
        handleLogout();
      }
    }, 1000);

    // Backup timer for logout
    warningTimerRef.current = setTimeout(handleLogout, warningTime);
  }, [warningTime, onWarning, handleLogout]);

  // Reset the idle timer
  const resetTimer = useCallback(() => {
    if (!enabled || !isAuthenticated) return;

    lastActivityRef.current = Date.now();
    clearAllTimers();

    // If we were in warning state, notify that user is active again
    if (isWarning) {
      setIsWarning(false);
      setRemainingSeconds(0);
      onActive?.();
    }

    setIsIdle(false);

    // Set timer for when to show warning
    const timeUntilWarning = idleTimeout - warningTime;

    idleTimerRef.current = setTimeout(() => {
      startWarningCountdown();
    }, timeUntilWarning);
  }, [enabled, isAuthenticated, clearAllTimers, isWarning, onActive, idleTimeout, warningTime, startWarningCountdown]);

  // Handle user activity
  const handleActivity = useCallback(() => {
    // Throttle activity detection to avoid excessive resets
    const now = Date.now();
    if (now - lastActivityRef.current < 1000) return; // Throttle to 1 second

    resetTimer();
  }, [resetTimer]);

  // Set up event listeners
  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      clearAllTimers();
      return;
    }

    // Initial timer setup
    resetTimer();

    // Add event listeners
    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Also listen for visibility changes (user switches tabs)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if we should have logged out while tab was hidden
        const elapsed = Date.now() - lastActivityRef.current;
        if (elapsed >= idleTimeout) {
          handleLogout();
        } else if (elapsed >= idleTimeout - warningTime) {
          startWarningCountdown();
        } else {
          resetTimer();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, isAuthenticated, handleActivity, resetTimer, clearAllTimers, idleTimeout, warningTime, handleLogout, startWarningCountdown]);

  return {
    isWarning,
    remainingSeconds,
    resetTimer,
    isIdle,
  };
}
